<template>
  <div v-if="pageLoading" class="page-content">
    <Loading />
  </div>
  <div :class="{ 'dark-mode': darkMode }" class="container">
    <div class="left">
      <div class="properties-menu">
        <PropertiesComponent />
      </div>
      <div style="z-index: 1">
        <form @submit.prevent="loginUser">
          <div class="logo-container">
            <img class="logo" src="@/assets/img/logo/logo.jpeg" alt="Logo" />
          </div>
          <div class="welcome-message">
            {{ $t("Login.welcome") }}
            <span class="highlight">{{ $t("APP_NAME") }}</span>
          </div>

          <label for="email">{{ $t("Login.email") }}</label>
          <input
            type="email"
            :placeholder="$t('Login.enterEmail')"
            name="email"
            v-model="email"
            :class="{ 'invalid-input': emailError }"
            required
          />

          <label for="password">{{ $t("Login.password") }}</label>
          <div class="password-input-container">
            <input
              :type="showPassword ? 'text' : 'password'"
              :placeholder="$t('Login.enterPassword')"
              name="password"
              v-model="password"
              :class="{ 'invalid-input': passwordError }"
              required
            />
            <span class="password-toggle" @click="togglePasswordVisibility">
              {{ showPassword ? "👁️" : "🙈" }}
            </span>
          </div>

          <button type="submit" class="login-btn" :disabled="loading">
            <span v-if="!loading">{{ $t("Login.name") }}</span>
            <span v-else class="spinner"></span>
          </button>

          <div class="links">
            <a href="#" @click.prevent="goToForgotPassword">{{
              $t("Login.forGotPassword")
            }}</a>
            <a href="#" @click.prevent="goToRegister">{{
              $t("Login.doNotHaveAnAccount")
            }}</a>
          </div>
        </form>
        <div class="or">{{ $t("Login.or") }}</div>
        <div class="btn-group">
          <GoogleLogin :callback="handleGoogleLogin" />
        </div>
      </div>
    </div>
    <div class="right">
      <div class="image-container">
        <img src="@/assets/img/auth/auth-login.png" />
      </div>
    </div>
  </div>

  <NotificationComponent
    v-if="notification.show"
    :key="notification.key"
    :type="notification.type"
    :title="notification.title"
    :message="notification.message"
    @closed="!notification.show"
  />
</template>

<script>
import PropertiesComponent from "@/components/user/layout/PropertiesComponent.vue";
import Loading from "@/components/user/layout/LoadingComponent.vue";
import { mapActions } from "vuex";
import NotificationComponent from "@/components/custom/notifications/NotificationComponent.vue";

export default {
  name: "LoginComponent",
  components: {
    PropertiesComponent,
    Loading,
    NotificationComponent,
  },
  data() {
    return {
      pageLoading: false,
      showPassword: false,
      darkMode: false,
      showTranslateMenu: false,
      email: "",
      password: "",
      passwordError: false,
      emailError: false,
      loginError: false,
      loginErrorMessage: "",
      loading: false,
      userProfile: {},
      notification: {
        show: false,
        key: 0,
        type: "",
        title: "",
        message: "",
      },
      user: null,
      isAuthenticated: false,
    };
  },
  computed: {
    isFormValid() {
      return this.password && this.email;
    },
  },
  methods: {
    ...mapActions({
      login: "auth/login",
      loginGoogle: "auth/loginGoogle",
    }),
    async loginUser() {
      this.loading = true;
      this.emailError = !this.email;
      this.passwordError = !this.password;

      if (this.emailError || this.passwordError) {
        this.emailError = !this.email;
        this.passwordError = !this.password;
        return;
      }

      const result = await this.login({
        email: this.email,
        password: this.password,
      });
      if (result.success) {
        this.changeLanguage(
          this.$i18n.locale,
          localStorage.getItem("localeTime")
        );
        this.$router.push("/dashboard");
      } else {
        this.showNotification("hold", this.$t("Custom.hold"), result.message);
        this.loginErrorMessage = result.message;
        this.emailError = true;
        this.passwordError = true;
      }

      this.loading = false;
    },
    async handleGoogleLogin(response) {
      this.pageLoading = true;
      if (response?.credential) {
        try {
          const result = await this.loginGoogle({
            cerdidential: response.credential,
          });
          if (result.success) {
            this.changeLanguage(
              this.$i18n.locale,
              localStorage.getItem("localeTime")
            );
            this.$router.push("/dashboard");
          } else {
            this.showNotification(
              "error",
              this.$t("Custom.error"),
              response.data.msg
            );
          }
        } catch (error) {
          this.showNotification(
            "hold",
            this.$t("Custom.hold"),
            response.error.data.message
          );
        } finally {
          this.pageLoading = false;
        }
      }
    },
    goToRegister() {
      this.$router.push({ name: "Register" });
    },
    goToForgotPassword() {
      this.$router.push({ name: "ForgotPassword" });
    },
    changeLanguage(lang = "en", localeTime = "en-US") {
      localeTime = localeTime || "en-US";
      this.$i18n.locale = lang;
      localStorage.setItem("lang", lang);
      localStorage.setItem("localeTime", localeTime);
    },
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    showNotification(type, title, message) {
      this.notification = {
        show: true,
        key: Date.now(),
        type,
        title,
        message,
      };
    },
  },
};
</script>

<style scoped>
/* Paste your CSS styles here */
@import url("https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;500;600;700&display=swap");

*,
*::before,
*::after {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Quicksand", sans-serif;
}

.container {
  display: flex;
  width: 100%;
  height: 100%;
}

.left {
  flex: 0 0 31%;
  height: 100%;
  padding: 7%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--background-color);
}

.right {
  flex: 1;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f4f5fa;
  border-radius: 25px;
  box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
}

.right .image-container {
  text-align: center;
}

.right img {
  max-width: 60%;
  height: 60%;
}

.logo-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 32px;
  width: 100%;
}

.logo {
  max-width: 100px;
  border-radius: 30%;
  height: auto;
}

.welcome-message {
  text-align: center;
  font-size: 1.2rem;
  margin-bottom: 32px;
}

.welcome-message .highlight {
  color: #f3754d;
}

.btn-group {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  gap: 5px;
  margin-bottom: 32px;
  width: 100%;
}

.btn-group .btn {
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 4px;
  width: 100%;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 8px 8px;
  border: 2px solid #6b7280;
  border-radius: 5px;
  background-color: white;
  transform: scale(1);
  cursor: pointer;
  transition: transform 0.1s ease, background-color 0.5s, color 0.5s;
}

.btn-group .btn:focus {
  transform: scale(0.97);
}

.btn-group .btn:hover {
  background-color: #000;
  color: #eee;
}

.btn img {
  width: 20px;
  height: 20px;
}

.logo {
  width: 40%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 30%;
  padding: 2px;
  margin: 8px;
}

/* OR */

.or {
  position: relative;
  text-align: center;
  margin-bottom: 30px;
  margin-top: 30px;
  font-size: 1rem;
  font-weight: 600;
}

.or::before,
.or::after {
  content: "";
  position: absolute;
  top: 50%;
  width: 40%;
  height: 1px;
  background: #000;
}

.or::before {
  left: 0;
}

.or::after {
  right: 0;
}

/* Inputs and Labels */

input {
  width: 100%;
  padding: 12px 20px;
  border: 2px solid #ccc;
  outline: 0;
  border-radius: 5px;
  background-color: transparent;
  margin: 4px 0 18px;
  font-size: 0.85rem;
  font-weight: 600;
  transition: all 0.5s;
}

input:focus {
  border: 2px solid #000;
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-background-clip: text;
  -webkit-text-fill-color: #000;
  transition: background-color 5000s ease-in-out 0s;
}

label {
  font-weight: 600;
  font-size: 0.9rem;
}

/* Login Button */

.login-btn {
  width: 100%;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 12px 24px;
  margin: 12px 0 24px;
  color: black;
  border: 2px solid #6b7280;
  border-radius: 5px;
  background-color: white;
  cursor: pointer;
  transition: all 0.5s;
  display: flex;
  justify-content: center;
  align-items: center;
}

.login-btn:hover {
  background-color: var(--logo-color);
  border-color: var(--logo-color);
  color: var(--text-white);
}

/* Links */

.links {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

a:link,
a:visited,
a:hover,
a:active {
  text-decoration: none;
}

a {
  color: #000;
  font-size: 0.88rem;
  font-weight: 600;
  letter-spacing: -1px;
  transition: all 0.4s ease;
}

a:hover {
  color: var(--logo-color);
}

.invalid-input {
  border: 2px solid red;
}

.error-message {
  color: red;
  margin-top: 10px;
}

/** Top Menu */
.properties-menu {
  position: fixed;
  top: 20px;
  left: 20px;
  width: 80px;
}

/* Medya Sorguları */
@media (max-width: 768px) {
  .container {
    flex-direction: column;
  }

  .left {
    position: fixed;
    flex: 0 0 100%;
    padding: 15%;
    width: 100%;
  }

  .right {
    display: none;
  }

  .logo {
    width: 29%;
  }
}
</style>
